import {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import {
  DoraEnvironment,
  DoraHistoryPoint,
  DoraMetrics,
  DoraMetricValue,
  DoraMetricsApi,
  DoraRating,
  DoraTargets,
  PrDetail,
} from './types';

const GITFLIC_READ_SCOPES = [
  'USER_READ',
  'PROJECT_READ',
  'TEAM_READ',
  'COMPANY_READ',
  'REPOSITORY_READ',
];

const GITFLIC_PROXY_PATH = '/gitflic/api';
const PAGE_SIZE = 100;

interface GitflicBranch {
  name: string;
}

interface GitflicBranchListResponse {
  _embedded?: {
    branchList?: GitflicBranch[];
  };
  page?: GitflicPage;
}

interface GitflicLabel {
  title: string;
}

interface GitflicUser {
  username: string;
  fullName?: string;
  avatar?: string;
}

interface GitflicReference {
  id: string;
  title: string;
}

interface GitflicMergeCommit {
  createdAt: string;
}

interface GitflicMergeRequest {
  id: string;
  localId: number;
  title: string;
  createdAt: string;
  updatedAt: string;
  labels: GitflicLabel[];
  createdBy: GitflicUser | null;
  targetBranch: GitflicReference;
  status: GitflicReference;
  mergeCommit: GitflicMergeCommit | null;
  projectAlias: string;
  userAlias: string;
}

interface GitflicMergeRequestListResponse {
  _embedded?: {
    mergeRequestModelList?: GitflicMergeRequest[];
  };
  page?: GitflicPage;
}

interface GitflicPage {
  size?: number;
  totalElements?: number;
  totalPages?: number;
  number?: number;
}

interface DoraMetricsClientOptions {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  gitflicAuthApi: OAuthApi;
}

/**
 * Compute a DORA rating based on the configured target.
 *
 * lowerIsBetter (lead time, CFR, MTTR, hotfix count):
 *   elite  = value <= target
 *   high   = value <= target * 2
 *   medium = value <= target * 4
 *   low    = value >  target * 4
 *
 * higherIsBetter (deployment frequency):
 *   elite  = value >= target
 *   high   = value >= target * 0.7
 *   medium = value >= target * 0.4
 *   low    = value <  target * 0.4
 *
 * Special case — target = 0 (hotfix count ideal = 0):
 *   elite=0, high<=2, medium<=5, low>5
 */
function computeRating(
  value: number,
  target: number,
  lowerIsBetter: boolean,
): DoraRating {
  if (lowerIsBetter) {
    if (target === 0) {
      if (value === 0) return 'elite';
      if (value <= 2) return 'high';
      if (value <= 5) return 'medium';
      return 'low';
    }
    if (value <= target) return 'elite';
    if (value <= target * 2) return 'high';
    if (value <= target * 4) return 'medium';
    return 'low';
  }
  if (value >= target) return 'elite';
  if (value >= target * 0.7) return 'high';
  if (value >= target * 0.4) return 'medium';
  return 'low';
}

function getMergedAt(mr: GitflicMergeRequest): string | undefined {
  return mr.mergeCommit?.createdAt;
}

function getMergeDurationHours(mr: GitflicMergeRequest): number {
  const mergedAt = getMergedAt(mr);

  if (!mergedAt) {
    return 0;
  }

  return (
    (new Date(mergedAt).getTime() - new Date(mr.createdAt).getTime()) /
    (1000 * 60 * 60)
  );
}

function getMergeRequestUrl(mr: GitflicMergeRequest): string {
  return `/project/${encodeURIComponent(mr.userAlias)}/${encodeURIComponent(
    mr.projectAlias,
  )}/merge-request/${mr.localId}`;
}

function toPrDetail(mr: GitflicMergeRequest, durationHours: number): PrDetail {
  return {
    number: mr.localId,
    title: mr.title,
    url: getMergeRequestUrl(mr),
    durationHours: Math.round(durationHours * 10) / 10,
    mergedAt: getMergedAt(mr)!,
    author: mr.createdBy?.username ?? mr.createdBy?.fullName,
    authorAvatar: mr.createdBy?.avatar,
  };
}

/**
 * Determine whether a pattern string is a regex (vs a plain name or
 * comma-separated list of names). A string is treated as a regex if it
 * contains any regex meta-character: | ^ $ [ ] ( ) { } ? + * \
 */
function isRegexPattern(pattern: string): boolean {
  return /[|^$[\](){}?+*\\]/.test(pattern);
}

/**
 * Return true if any of the MR's labels match the label pattern.
 *
 * Supports the same three formats as branch patterns:
 *   - Single name:       "hotfix"
 *   - Comma-separated:  "hotfix,fix,bug"
 *   - JavaScript regex: "^(hotfix|fix)$"
 */
function matchesLabel(
  mrLabels: Array<{ title: string }>,
  labelPattern: string,
): boolean {
  if (!labelPattern) return false;
  const titles = mrLabels.map(l => l.title);
  if (isRegexPattern(labelPattern)) {
    const regex = new RegExp(labelPattern, 'i');
    return titles.some(n => regex.test(n));
  }
  const patterns = labelPattern
    .split(',')
    .map(l => l.trim().toLowerCase())
    .filter(Boolean);
  return titles.some(n => patterns.includes(n.toLowerCase()));
}

async function resolveBranches(
  ownerAlias: string,
  projectAlias: string,
  branchPattern: string,
  fetchFn: <T>(path: string) => Promise<T>,
): Promise<string[]> {
  if (isRegexPattern(branchPattern)) {
    const branches = await fetchAllPages<
      GitflicBranch,
      GitflicBranchListResponse
    >(
      page =>
        fetchFn<GitflicBranchListResponse>(
          `/project/${encodeURIComponent(ownerAlias)}/${encodeURIComponent(
            projectAlias,
          )}/branch?page=${page}&size=${PAGE_SIZE}`,
        ),
      response => response._embedded?.branchList ?? [],
    );
    const regex = new RegExp(branchPattern);
    return branches.map(b => b.name).filter(name => regex.test(name));
  }

  return branchPattern
    .split(',')
    .map(b => b.trim())
    .filter(Boolean);
}

async function fetchAllPages<T, TResponse extends { page?: GitflicPage }>(
  fetchPage: (page: number) => Promise<TResponse>,
  getItems: (response: TResponse) => T[],
): Promise<T[]> {
  const items: T[] = [];

  for (let page = 0; ; page++) {
    const response = await fetchPage(page);
    items.push(...getItems(response));

    const totalPages = response.page?.totalPages;
    if (totalPages !== undefined) {
      if (page + 1 >= totalPages) {
        break;
      }
      continue;
    }

    if (getItems(response).length < PAGE_SIZE) {
      break;
    }
  }

  return items;
}

export class DoraMetricsClient implements DoraMetricsApi {
  private readonly configApi: ConfigApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly gitflicAuthApi: OAuthApi;

  constructor(options: DoraMetricsClientOptions) {
    this.configApi = options.configApi;
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.gitflicAuthApi = options.gitflicAuthApi;
  }

  private getConfig() {
    const appCfg = this.configApi.getConfig('app');
    const cfg = appCfg.getOptionalConfig('doraMetrics');

    const rawEnvs = cfg?.getOptionalConfigArray('environments') ?? [];
    const environments: DoraEnvironment[] = rawEnvs.map(e => ({
      name: e.getString('name'),
      branch: e.getString('branch'),
      isProduction: e.getOptionalBoolean('isProduction') ?? false,
      label: e.getOptionalString('label') ?? 'hotfix',
    }));

    // Validate: at most one isProduction environment
    const prodEnvs = environments.filter(e => e.isProduction);
    if (prodEnvs.length > 1) {
      throw new Error(
        `DORA metrics config error: only one environment may have isProduction: true, ` +
          `but found ${prodEnvs.length}: ${prodEnvs
            .map(e => e.name)
            .join(', ')}`,
      );
    }

    const initialDays =
      cfg?.getOptionalConfig('collection')?.getOptionalNumber('initialDays') ??
      30;

    const targetsCfg = cfg?.getOptionalConfig('targets');
    const targets: DoraTargets = {
      deploymentFrequency:
        targetsCfg?.getOptionalNumber('deploymentFrequency') ?? 7,
      leadTime: targetsCfg?.getOptionalNumber('leadTime') ?? 24,
      changeFailureRate:
        targetsCfg?.getOptionalNumber('changeFailureRate') ?? 15,
      mttr: targetsCfg?.getOptionalNumber('mttr') ?? 1,
    };

    return { environments, initialDays, targets };
  }

  getEnvironments(): DoraEnvironment[] {
    return this.getConfig().environments;
  }

  getDefaultDays(): number {
    return this.getConfig().initialDays;
  }

  getTargets(): DoraTargets {
    return this.getConfig().targets;
  }

  private async fetchWithAuth<T>(path: string, token: string): Promise<T> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const response = await this.fetchApi.fetch(
      `${proxyBaseUrl}${GITFLIC_PROXY_PATH}${path}`,
      {
        headers: {
          Authorization: `token ${token}`,
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, path));
    }

    return (await response.json()) as T;
  }

  private async fetchMergedMRs(
    ownerAlias: string,
    projectAlias: string,
    env: DoraEnvironment,
    cutoff: Date,
    token: string,
  ): Promise<GitflicMergeRequest[]> {
    const boundFetch = <T>(path: string) => this.fetchWithAuth<T>(path, token);

    const branches = await resolveBranches(
      ownerAlias,
      projectAlias,
      env.branch,
      boundFetch,
    );

    const allByLocalId = new Map<number, GitflicMergeRequest>();
    const mrs = await fetchAllPages<
      GitflicMergeRequest,
      GitflicMergeRequestListResponse
    >(
      page =>
        boundFetch<GitflicMergeRequestListResponse>(
          `/project/${encodeURIComponent(ownerAlias)}/${encodeURIComponent(
            projectAlias,
          )}/merge-request/list?page=${page}&size=${PAGE_SIZE}`,
        ),
      response => response._embedded?.mergeRequestModelList ?? [],
    );

    for (const mr of mrs) {
      const mergedAt = getMergedAt(mr);

      if (
        mr.status.id === 'MERGED' &&
        mergedAt &&
        new Date(mergedAt) >= cutoff &&
        branches.includes(mr.targetBranch.title)
      ) {
        allByLocalId.set(mr.localId, mr);
      }
    }

    return Array.from(allByLocalId.values());
  }

  async getMetrics(
    projectSlug: string,
    env: DoraEnvironment,
    days: number,
    targetsOverride?: Partial<DoraTargets>,
  ): Promise<DoraMetrics> {
    const token = await this.gitflicAuthApi.getAccessToken(GITFLIC_READ_SCOPES);
    const { targets: configTargets } = this.getConfig();
    const targets: DoraTargets = { ...configTargets, ...targetsOverride };

    const { ownerAlias, projectAlias } = this.parseProjectSlug(projectSlug);
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const mergedMRs = await this.fetchMergedMRs(
      ownerAlias,
      projectAlias,
      env,
      cutoff,
      token,
    );

    // --- Deployment Frequency ---
    const deploymentsPerWeek = days > 0 ? (mergedMRs.length / days) * 7 : 0;

    const deploymentFrequency: DoraMetricValue = {
      value: Math.round(deploymentsPerWeek * 100) / 100,
      unit: 'per week',
      rating: computeRating(
        deploymentsPerWeek,
        targets.deploymentFrequency,
        false,
      ),
      target: targets.deploymentFrequency,
      slowestPRs: [...mergedMRs]
        .sort(
          (a, b) =>
            new Date(getMergedAt(b)!).getTime() -
            new Date(getMergedAt(a)!).getTime(),
        )
        .map(mr => toPrDetail(mr, getMergeDurationHours(mr))),
    };

    // --- Lead Time ---
    const mrsWithLeadTime = mergedMRs
      .map(mr => ({
        mr,
        durationHours: getMergeDurationHours(mr),
      }))
      .sort((a, b) => b.durationHours - a.durationHours);

    const avgLeadTimeHours =
      mrsWithLeadTime.length > 0
        ? mrsWithLeadTime.reduce((sum, p) => sum + p.durationHours, 0) /
          mrsWithLeadTime.length
        : 0;

    const leadTime: DoraMetricValue = {
      value: Math.round(avgLeadTimeHours * 10) / 10,
      unit: 'hours',
      rating: computeRating(avgLeadTimeHours, targets.leadTime, true),
      target: targets.leadTime,
      slowestPRs: mrsWithLeadTime
        .slice(0, 10)
        .map(p => toPrDetail(p.mr, p.durationHours)),
    };

    // --- Production-only metrics ---
    if (!env.isProduction) {
      return {
        deploymentFrequency,
        leadTime,
        changeFailureRate: null,
        mttr: null,
        numberOfHotfixes: null,
      };
    }

    const hotfixMRs = mergedMRs.filter(mr =>
      matchesLabel(mr.labels ?? [], env.label),
    );

    const numberOfHotfixes: DoraMetricValue = {
      value: hotfixMRs.length,
      unit: 'MRs',
      rating: computeRating(hotfixMRs.length, 0, true),
      target: 0,
      slowestPRs: [...hotfixMRs]
        .sort(
          (a, b) =>
            new Date(getMergedAt(b)!).getTime() -
            new Date(getMergedAt(a)!).getTime(),
        )
        .map(mr => toPrDetail(mr, getMergeDurationHours(mr))),
    };

    const changeFailureRatePct =
      mergedMRs.length > 0 ? (hotfixMRs.length / mergedMRs.length) * 100 : 0;

    const changeFailureRate: DoraMetricValue = {
      value: Math.round(changeFailureRatePct * 10) / 10,
      unit: '%',
      rating: computeRating(
        changeFailureRatePct,
        targets.changeFailureRate,
        true,
      ),
      target: targets.changeFailureRate,
    };

    // MTTR
    const hotfixWithDuration = hotfixMRs
      .map(mr => ({
        mr,
        durationHours: getMergeDurationHours(mr),
      }))
      .sort((a, b) => b.durationHours - a.durationHours);

    const avgMttrHours =
      hotfixWithDuration.length > 0
        ? hotfixWithDuration.reduce((sum, p) => sum + p.durationHours, 0) /
          hotfixWithDuration.length
        : 0;

    const mttr: DoraMetricValue = {
      value: Math.round(avgMttrHours * 10) / 10,
      unit: 'hours',
      rating: computeRating(avgMttrHours, targets.mttr, true),
      target: targets.mttr,
      slowestPRs: hotfixWithDuration
        .slice(0, 10)
        .map(p => toPrDetail(p.mr, p.durationHours)),
    };

    return {
      deploymentFrequency,
      leadTime,
      changeFailureRate,
      mttr,
      numberOfHotfixes,
    };
  }

  async getHistory(
    projectSlug: string,
    env: DoraEnvironment,
    days: number,
  ): Promise<DoraHistoryPoint[]> {
    const token = await this.gitflicAuthApi.getAccessToken(GITFLIC_READ_SCOPES);
    const { ownerAlias, projectAlias } = this.parseProjectSlug(projectSlug);

    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const mergedMRs = await this.fetchMergedMRs(
      ownerAlias,
      projectAlias,
      env,
      cutoff,
      token,
    );

    // Scale bucket size so we always get ~7 data points regardless of date range
    const bucketDays = Math.max(1, Math.round(days / 7));
    const numBuckets = Math.max(2, Math.ceil(days / bucketDays));
    const buckets: DoraHistoryPoint[] = [];

    for (let w = numBuckets - 1; w >= 0; w--) {
      const weekEnd = new Date(
        Date.now() - w * bucketDays * 24 * 60 * 60 * 1000,
      );
      const weekStart = new Date(
        weekEnd.getTime() - bucketDays * 24 * 60 * 60 * 1000,
      );

      const weekMRs = mergedMRs.filter(mr => {
        const t = new Date(getMergedAt(mr)!).getTime();
        return t >= weekStart.getTime() && t < weekEnd.getTime();
      });

      const leadTimes = weekMRs.map(mr => getMergeDurationHours(mr));
      const avgLeadTime =
        leadTimes.length > 0
          ? leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length
          : 0;

      let changeFailureRate: number | undefined;
      let mttrHours: number | undefined;

      if (env.isProduction) {
        const hotfixMRs = weekMRs.filter(mr =>
          matchesLabel(mr.labels, env.label),
        );
        changeFailureRate =
          weekMRs.length > 0
            ? Math.round((hotfixMRs.length / weekMRs.length) * 1000) / 10
            : 0;
        const hotfixDurations = hotfixMRs.map(mr => getMergeDurationHours(mr));
        mttrHours =
          hotfixDurations.length > 0
            ? Math.round(
                (hotfixDurations.reduce((a, b) => a + b, 0) /
                  hotfixDurations.length) *
                  10,
              ) / 10
            : 0;
      }

      const labelDate = new Date(Math.min(weekEnd.getTime(), Date.now()));
      buckets.push({
        weekLabel: labelDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        bucketMidMs: Math.round((weekStart.getTime() + weekEnd.getTime()) / 2),
        deploymentCount: weekMRs.length,
        leadTimeHours: Math.round(avgLeadTime * 10) / 10,
        changeFailureRate,
        mttrHours,
      });
    }

    return buckets;
  }

  private parseProjectSlug(projectSlug: string): {
    ownerAlias: string;
    projectAlias: string;
  } {
    const [ownerAlias, projectAlias, ...rest] = projectSlug.split('/');

    if (!ownerAlias || !projectAlias || rest.length > 0) {
      throw new Error(
        `Expected Gitflic project slug in format owner/project, got "${projectSlug}"`,
      );
    }

    return { ownerAlias, projectAlias };
  }

  private async buildErrorMessage(
    response: Response,
    path: string,
  ): Promise<string> {
    const fallback = `Gitflic API request failed: ${response.status} ${response.statusText} - ${path}`;
    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      try {
        const body = await response.json();
        const message =
          body?.error?.message ?? body?.message ?? body?.error ?? body?.detail;

        if (message) {
          return `${fallback}: ${message}`;
        }
      } catch {
        return fallback;
      }
    }

    try {
      const text = await response.text();
      return text ? `${fallback}: ${text}` : fallback;
    } catch {
      return fallback;
    }
  }
}
